import { DatePipe } from '@angular/common';
import { Component, computed, ElementRef, HostListener, inject, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthorizationService } from '@app/core/auth/authorization.service';
import { IsBlvDirective } from '@app/core/auth/is-blv.directive';
import { IsTierarztDirective } from '@app/core/auth/is-tierarzt.directive';
import { DokumentComponent } from '@app/dokument/dokument.component';
import { MeldungBewilligenDialogComponent } from '@app/meldung/meldung-bewilligen-dialog/meldung-bewilligen-dialog.component';
import { MeldungDeaktivierenDialogComponent } from '@app/meldung/meldung-deaktivieren-dialog/meldung-deaktivieren-dialog.component';
import { MeldungHistoryDialogComponent } from '@app/meldung/meldung-history-dialog/meldung-history-dialog.component';
import { MeldungNotizBlvEditComponent } from '@app/meldung/meldung-notiz-blv-edit/meldung-notiz-blv-edit.component';
import { MeldungNotizKantonEditComponent } from '@app/meldung/meldung-notiz-kanton-edit/meldung-notiz-kanton-edit.component';
import { Meldung } from '@app/shared/classes/meldung';
import { MeldungArtId } from '@app/shared/classes/meldung-art';
import { NutzungId } from '@app/shared/classes/nutzung';
import { StatusId } from '@app/shared/classes/status';
import { downloadFile } from '@app/shared/download-utils';
import { DropdownDirective } from '@app/shared/dropdown.directive';
import { InjectUrlPipe } from '@app/shared/pipes/inject-url.pipe';
import { MarkedPipe } from '@app/shared/pipes/marked.pipe';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs/operators";

enum MessageType {
  NONE,
  MELDUNG_ERSTELLT,
  MELDUNG_AKTIV,
  ANTRAG_ERSTELLT,
  ANTRAG_IN_ARBEIT,
  ANTRAG_BEWILLIGT
}

namespace MessageType {
  export function getClass(type: MessageType): string {
    if (type === MessageType.MELDUNG_ERSTELLT
      || type === MessageType.MELDUNG_AKTIV
      || type === MessageType.ANTRAG_BEWILLIGT) {
      return 'alert alert-success';
    } else if (type === MessageType.ANTRAG_ERSTELLT
      || type === MessageType.ANTRAG_IN_ARBEIT) {
      return 'alert alert-warning';
    }
    return '';
  }

  export function getMessageCode(type: MessageType): string {
    switch(type) {
      case MessageType.MELDUNG_ERSTELLT : return 'meldung.view.message.frei.erstellt';
      case MessageType.MELDUNG_AKTIV    : return 'meldung.view.message.frei.aktiv';
      case MessageType.ANTRAG_ERSTELLT  : return 'meldung.view.message.pflichtig.erstellt';
      case MessageType.ANTRAG_IN_ARBEIT : return 'meldung.view.message.pflichtig.inArbeit';
      case MessageType.ANTRAG_BEWILLIGT : return 'meldung.view.message.pflichtig.bewilligt';
      default                           : return '';
    }
  }
}

@Component({
  selector: 'app-meldung',
  templateUrl: './meldung.component.html',
  imports: [
    DatePipe,
    DokumentComponent,
    DropdownDirective,
    FormsModule,
    InjectUrlPipe,
    IsBlvDirective,
    IsTierarztDirective,
    MarkedPipe,
    MatTooltip,
    MeldungBewilligenDialogComponent,
    MeldungDeaktivierenDialogComponent,
    MeldungHistoryDialogComponent,
    MeldungNotizBlvEditComponent,
    MeldungNotizKantonEditComponent,
    RouterLink,
    TranslatePipe,
  ]
})
export class MeldungComponent {
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly authorizationService = inject(AuthorizationService);

  private readonly meldungId = toSignal(this.route.paramMap.pipe(map(params => params.get('id'))))
  private readonly neuErstellt = toSignal(this.route.queryParams.pipe(map(params => 'created' in params || !!globalThis.history.state?.created)))

  preventRedirect : boolean = false;
  @ViewChild('notizBlvEdit')       notizBlvEdit       !: ElementRef;
  @ViewChild('notizKantonEdit')    notizKantonEdit    !: ElementRef;
  @ViewChild('historyDialog')      historyDialog      !: ElementRef;
  @ViewChild('bewilligenDialog')   bewilligenDialog   !: ElementRef;
  @ViewChild('deaktivierenDialog') deaktivierenDialog !: ElementRef;

  meldung = rxResource({
    params: () => {
      const id = this.meldungId();
      return id ? { id } : undefined;
    },
    stream: ({ params }) => this.backendService.getMeldung(params.id)
  });

  stornoDisabled = computed(() => {
    const meldung = this.meldung.value();
    return meldung?.status?.id === StatusId.STORNIERT
        || meldung?.status?.id === StatusId.STORNO_ANFRAGE && this.authorizationService.isTierarzt();
  });

  showBewilligeMeldung = computed(() => {
    const meldung = this.meldung.value();
    return meldung?.status?.id === StatusId.OFFEN && this.authorizationService.isBlv();
  });

  showMessage = computed(() => {
    const meldung = this.meldung.value();
    if (meldung) {
      const bewilligungsPflichtig = meldung.requiredDokumente?.bewilligungsPflichtig;
      if (this.neuErstellt()) {
        return bewilligungsPflichtig ? MessageType.ANTRAG_ERSTELLT : MessageType.MELDUNG_ERSTELLT;
      } else if (meldung.status?.id === StatusId.OFFEN && bewilligungsPflichtig) {
        return MessageType.ANTRAG_IN_ARBEIT;
      } else if (StatusId.isBewilligt(meldung.status?.id)) {
        return bewilligungsPflichtig ? MessageType.ANTRAG_BEWILLIGT : MessageType.MELDUNG_AKTIV;
      }
    }
    return MessageType.NONE
  });

  downloadPdf(): void {
    const meldung = this.meldung.value();
    if (!meldung) {
      throw new Error("No Meldung selected");
    }
    this.backendService.getMeldungPdf(meldung.id)
      .subscribe({
        next: downloadFile,
        error: console.error
      });
  }

  downloadBewilligung(): void {
    const meldung = this.meldung.value();
    if (!meldung) {
      throw new Error("No Meldung selected");
    }
    this.backendService.downloadBewilligung(meldung.id)
      .subscribe({
        next: downloadFile,
        error: console.error
      });
  }

  async updateMeldung(meldung: Meldung): Promise<void> {
    this.meldung.value.set(meldung);
  }

  deactivateMeldung(): void {
    Swal.fire({
      title: this.translateService.instant('meldung.view.deaktivieren.title'),
      html: this.deaktivierenDialog.nativeElement,
      width: 'auto',
      showConfirmButton: false
    }).then((result) =>console.log(result));
  }

  bewilligeMeldung(): void {
    Swal.fire({
      title: this.translateService.instant('meldung.view.bewilligen.title'),
      html: this.bewilligenDialog.nativeElement,
      width: 'auto',
      showConfirmButton: false
    }).then((result) => console.log(result));
  }

  editNotizBlv(): void {
    Swal.fire({
      html: this.notizBlvEdit.nativeElement,
      width: 'auto',
      showConfirmButton: false
    }).then((result) => console.log(result));
  }

  editNotizKanton(): void {
    Swal.fire({
      html: this.notizKantonEdit.nativeElement,
      width: 'auto',
      showConfirmButton: false
    }).then((result) => console.log(result));
  }

  showHistory(): void {
    Swal.fire({
      title: this.translateService.instant('meldung.view.history.title'),
      html: this.historyDialog.nativeElement,
      width: 'auto',
      showConfirmButton: false
    }).then((result) => console.log(result));
  }

  get showAuslaendischeZulassungIndikation(): boolean {
    const meldung = this.meldung.value();
    return !! meldung?.praeparat?.auslaendischeZulassungTierart;
  }

  get showImmunologischFelder(): boolean {
    const meldung = this.meldung.value();
    return !! meldung && meldung.art.id > MeldungArtId.ALPHA && meldung.praeparat.immunologisch;
  }

  get showNotizBlv(): boolean {
    const meldung = this.meldung.value();
    return !!(meldung?.notiz?.notizBlv || meldung?.notiz?.offeneAnfrage)
      && this.authorizationService.isBlv()
  }

  get showNotizKanton(): boolean {
    const meldung = this.meldung.value();
    return !!meldung?.notiz?.notizKanton
      && this.authorizationService.isBlv()
  }

  get isStorniert(): boolean {
    const meldung = this.meldung.value();
    return meldung?.status?.id === StatusId.STORNIERT;
  }

  get isExtendedPdf(): boolean {
    const meldung = this.meldung.value();
    return !! meldung?.requiredDokumente?.bewilligungVorhanden;
  }

  get isStornoAnfrage(): boolean {
    const meldung = this.meldung.value();
    return meldung?.status?.id === StatusId.STORNO_ANFRAGE;
  }

  get showAlertBewilligungsPflichtig(): boolean {
    const meldung = this.meldung.value();
    if (!meldung) {
      return false;
    }

    const zulassungsLand = meldung.praeparat.zulassungsLand;
    const nutzung = meldung.praeparat.nutzung;

    if (meldung.art.id < MeldungArtId.BETA) {
      return (this.showAuslaendischeZulassungIndikation && !meldung.praeparat?.auslaendischeZulassungIndikation)
        || !meldung.praeparat?.auslaendischeZulassungTierart
        || meldung.praeparat?.anhang5
        || meldung.praeparat?.bienen
        || (zulassungsLand && nutzung && !zulassungsLand.erlaubteNutzung.includes(nutzung.id))
        || meldung.praeparat?.immunologisch;
    }

    return (meldung.praeparat?.umgewidmet && nutzung.id === NutzungId.NUTZTIER)
      || meldung.praeparat?.anhang5
      || meldung.praeparat?.bienen
      || (zulassungsLand && nutzung && !zulassungsLand.erlaubteNutzung.includes(nutzung.id))
      || meldung.praeparat?.immunologisch;
  }

  get titleCode(): string {
    const meldung = this.meldung.value();
    if (!meldung?.requiredDokumente?.bewilligungsPflichtig) {
      return 'meldung.view.title.frei';
    } else if (meldung?.requiredDokumente?.bewilligungVorhanden) {
      return 'meldung.view.title.pflichtig.bewilligt';
    } else {
      return 'meldung.view.title.pflichtig.antrag';
    }
  }

  get bestaetigungCode(): string {
    const meldung = this.meldung.value();
    const meldungArt = meldung?.art?.id ?? MeldungArtId.GAMMA;
    return meldungArt < MeldungArtId.GAMMA
      ? 'meldung.feld.bestaetigung.beta'
      : 'meldung.feld.bestaetigung.gamma';
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (this.preventRedirect) {
      return Swal.fire({
        text: this.translateService.instant('dokument.unsaved.message'),
        icon: 'warning',
        buttonsStyling: true,
        showCancelButton: true,
        confirmButtonText: this.translateService.instant('global.yes'),
        cancelButtonText: this.translateService.instant('global.no')
      }).then( (result) => result.isConfirmed );
    }
    return true;
  };

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (this.preventRedirect) {
      event.preventDefault();
    }
  }

  protected readonly MeldungArtId = MeldungArtId;
  protected readonly MessageType = MessageType;
}
